import pick from 'lodash/pick';
import flow from 'lodash/flow';
import { multipartFieldNames } from '../prefillAnswers';
import isObject from 'lodash/isObject';

const persistState = key => store => next => action => {
  const returnValue = next(action);
  saveState(store.getState(), key);
  return returnValue;
};

export default persistState;

const serializeState = state => JSON.stringify(state);

const saveState = (state, key) => {
  if ('sessionStorage' in window) {
    const tmap = flow(getQuestions, tmapQuestions)(state);
    try {
      window.sessionStorage.setItem(key, serializeState(tmap));
    } catch (e) {
      if (isQuotaExceeded(e)) {
        window.sessionStorage.clear();
      }
    }
  }
};

function getQuestions(state) {
  const questionKeys = Object.keys(state)
    .filter(key => key.match(/question_/))
    .filter(key => !!state[key]);
  return pick(state, questionKeys);
}

const tmapQuestions = questions => {
  return Object.keys(questions).reduce((tmap, key) => {
    const question = questions[key];
    tmap[question.name] = tmapQuestion(question);
    tmap[`${question.name}-other-value`] = tmapOther(question);
    tmap[`${question.name}-why`] = tmapWhy(question);
    return tmap;
  }, {});
};

const tmapQuestion = question => {
  switch (question.type) {
    case 'Survey::MultiPart':
      return tmapMultipart(question);
    case 'Survey::HeatMap':
    case 'Survey::TimedHeatMap':
    case 'Survey::StickyNote':
      return tmapHeatmap(question);
    default:
      return 'rows' in question ? tmapMatrix(question) : tmapSimple(question);
  }
};

const tmapSimple = question => {
  return isObject(question) && 'values' in question
    ? tmapValues(question.values)
    : [question.value];
};

const tmapMatrix = ({ rows }) => {
  return rows.reduce((tmap, { name, ...row }) => {
    tmap[name] = tmapSimple(row);
    tmap[`${name}-why`] = tmapWhy(row);
    return tmap;
  }, {});
};

const tmapValues = values => {
  // In some cases, values could be an array of strings, but in others values is
  // an object with a values key and a why key.
  if (isObject(values) && 'values' in values) {
    return tmapValues(values.values);
  }

  return values.length === 0
    ? values
    : typeof values[0] === 'string' ? values : values.map(v => v.value);
};

const tmapOther = ({ otherValue }) => [otherValue || ''];

const tmapWhy = ({ why }) => (why ? [why.value || ''] : ['']);

const tmapMultipart = ({ composite_type, values }) => {
  const fieldNames = multipartFieldNames(composite_type);
  return fieldNames.reduce(
    (acc, field, index) => ({
      ...acc,
      [field]: values[index].value,
    }),
    {}
  );
};

const tmapHeatmap = ({ markers, notes, rows, times }) => {
  return rows.reduce((acc, { keys, name }) => {
    acc[name] = tmapHeatmapRow({ keys, markers, notes, times });
    return acc;
  }, {});
};

const tmapHeatmapRow = ({ keys, markers, notes, times }) => {
  const x = keys.map(key => markers[key].x);
  const y = keys.map(key => markers[key].y);
  const comment = keys.map(key => notes[key]);
  const time = keys.map(key => times[key]);
  return { x, y, comment, time };
};

function isQuotaExceeded(e) {
  var quotaExceeded = false;
  if (e) {
    if (e.code) {
      switch (e.code) {
        case 22:
          quotaExceeded = true;
          break;
        case 1014:
          // Firefox
          if (e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            quotaExceeded = true;
          }
          break;
      }
    } else if (e.number === -2147024882) {
      // Internet Explorer 8
      quotaExceeded = true;
    }
  }
  return quotaExceeded;
}
